import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import EngineeringIcon from "@mui/icons-material/Engineering";

const AboutUs = () => {
  const theme = useTheme();

  const backgroundGradient = `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`;

  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          py: 12,
          background: backgroundGradient,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage:
              "radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 10%)",
            backgroundSize: "20px 20px",
            backgroundRepeat: "repeat",
            opacity: 0.4,
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box>
                <Typography
                  variant="overline"
                  component="span"
                  color="primary.main"
                  fontWeight={600}
                  sx={{
                    borderRadius: 1,
                    pl: 1,
                    pr: 1,
                    py: 0.5,
                    background: alpha(theme.palette.primary.main, 0.1),
                    mb: 2,
                    display: "inline-block",
                  }}
                >
                  ESTABLISHED 2024
                </Typography>
                <Typography
                  variant="h1"
                  component="h1"
                  color="text.primary"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    mb: 2,
                    lineHeight: 1.1,
                  }}
                >
                  About{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "primary.main",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: -4,
                        left: 0,
                        height: "4px",
                        width: "100%",
                        backgroundColor: "primary.main",
                        opacity: 0.6,
                        borderRadius: "4px",
                      },
                    }}
                  >
                    ProtoDiag
                  </Box>{" "}
                  Solutions
                </Typography>
                <Typography
                  variant="h5"
                  component="h2"
                  color="text.secondary"
                  sx={{ mb: 4, fontWeight: 400, maxWidth: "90%" }}
                >
                  Empowering the Future of Telecom Communication with
                  Innovation, Integrity, and Customer Success.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  href="/contact"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  Connect With Us
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src="/assets/images/TelecomImage.jpg"
                alt="Telecom technology illustration"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: { xs: "none", md: "block" },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Company Vision & Mission */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.03
                )} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  opacity: 0.04,
                  transform: "rotate(10deg)",
                  zIndex: 0,
                }}
              >
                <TravelExploreIcon sx={{ fontSize: 200 }} />
              </Box>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 2,
                  }}
                >
                  <TravelExploreIcon sx={{ mr: 1 }} /> Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 2, fontStyle: "italic", fontWeight: 500 }}
                >
                  "To be the global catalyst for next-generation
                  communication—enabling intelligent, secure, and real-time
                  connectivity across networks, platforms, and people."
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body1">
                  We envision a world where digital communication is
                  limitless—transcending barriers of scale, complexity, and
                  geography. ProtoDiag is committed to building intelligent,
                  interconnected ecosystems where businesses, telecom operators,
                  and users experience seamless and secure communication.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.03
                )} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  opacity: 0.04,
                  transform: "rotate(10deg)",
                  zIndex: 0,
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 200 }} />
              </Box>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 2,
                  }}
                >
                  <RocketLaunchIcon sx={{ mr: 1 }} /> Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 2, fontStyle: "italic", fontWeight: 500 }}
                >
                  "To empower enterprises and telecom operators with scalable,
                  API-first communication technologies—driving innovation,
                  reliability, and compliance in voice, messaging, and digital
                  engagement ecosystems."
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body1">
                  Our mission is to simplify and accelerate communication by
                  offering robust, carrier-grade platforms tailored for high
                  throughput, security, and compliance. Through real-time APIs,
                  intelligent routing engines, and AI-enhanced services, we
                  ensure that our partners stay ahead in delivering
                  mission-critical communication experiences globally.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Company Story */}
      <Box sx={{ py: 8, background: backgroundGradient }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Box>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Our Story
                </Typography>
                <Typography variant="body1" paragraph>
                  Founded in 2024, ProtoDiag Solutions Pvt. Ltd. is a telecom
                  technology company based in Noida, India, focused on
                  developing next-generation communication infrastructure for
                  enterprises and mobile network operators.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our journey began with a simple mission: to simplify and
                  accelerate communication by offering robust, carrier-grade
                  platforms tailored for high throughput, security, and
                  compliance.
                </Typography>
                <Typography variant="body1" paragraph>
                  Today, we're proud to be at the forefront of telecom
                  innovation, providing solutions that handle massive volumes
                  (10K+ TPS), integrate seamlessly with operator backbones, and
                  offer intelligent, API-first capabilities for modern digital
                  communication.
                </Typography>
                <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      textAlign: "center",
                      width: { xs: "100%", sm: "45%" },
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Typography
                      variant="h3"
                      component="p"
                      color="primary.main"
                      fontWeight={700}
                    >
                      100K+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      TPS Throughput Capacity
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      textAlign: "center",
                      width: { xs: "100%", sm: "45%" },
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Typography
                      variant="h3"
                      component="p"
                      color="primary.main"
                      fontWeight={700}
                    >
                      600K+
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      SMS Per Minute Capacity
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 300, md: 500 },
                  background:
                    "url('/assets/images/OurJourny.jpg') center/cover no-repeat",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: theme.shadows[10],
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.2)",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What We Do */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            What We Do
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto", fontWeight: 400 }}
          >
            We specialize in developing high-performance telecom solutions for
            enterprises and operators
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  High-TPS Messaging
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                High-performance messaging platforms capable of processing over
                100,000 transactions per second with real-time delivery
                tracking.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  SMSC Solutions
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Application-layer SMSC solutions that integrate directly with
                mobile operator infrastructure for optimal message delivery.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  AI-Powered Systems
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Intelligent spam detection and IVR systems that leverage AI to
                enhance communication security and user experience.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Analytics & Reporting
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Real-time DLR/CDR generation and analytics tools that provide
                actionable insights on messaging performance.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Compliance Solutions
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Comprehensive compliance services to ensure your communications
                align with industry standards and legal requirements.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: "primary.main",
                }}
              >
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Voice Communications
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Advanced voice delivery platforms with IVR capabilities, call
                bridging, and multi-lingual support for diverse communication
                needs.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          py: 8,
          color: "white",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{ mb: 3, fontWeight: 700 }}
            >
              Ready to Transform Your Telecom Solutions?
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}
            >
              Join the leaders who trust ProtoDiag for high-performance telecom
              infrastructure
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="/contact"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
                boxShadow: theme.shadows[10],
                background: "white",
                color: "primary.main",
                // "&:hover": {
                //   background: alpha("white", 0.9),
                // },
              }}
            >
              Schedule a Consultation
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;
